/**
 * Password field with a show/hide toggle.
 *
 * Wraps the design-system `Input` so the toggle button doesn't have to be
 * reinvented in every auth screen. The toggle uses `TapTarget` (button under
 * the hood, never submits the parent form) and `onMouseDown.preventDefault()`
 * keeps focus on the input after a click.
 */

import { type ComponentProps, useState } from "react";

import { t } from "../i18n";
import { Icon, Input, TapTarget } from "./ui";

export interface PasswordInputProps extends Omit<ComponentProps<typeof Input>, "type"> {
  /** Override for the toggle button's aria-label. Defaults to localized "show password". */
  showLabel?: string;
  hideLabel?: string;
}

export function PasswordInput({
  showLabel = t.common.showPassword,
  hideLabel = t.common.hidePassword,
  className,
  ...props
}: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input
        {...props}
        type={visible ? "text" : "password"}
        className={`${className ?? ""} pr-10`.trim()}
      />
      <TapTarget
        aria-label={visible ? hideLabel : showLabel}
        aria-pressed={visible}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setVisible((v) => !v)}
        className="absolute top-1/2 right-2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-ink-500 hover:bg-ink-100 hover:text-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30"
      >
        <Icon name={visible ? "eye-off" : "eye"} size="sm" />
      </TapTarget>
    </div>
  );
}
